import type {
  PersonAssociate,
  PersonContactInfo,
  PersonEducation,
  PersonJob,
  PersonLicense,
  PersonNeighbor,
  PersonRealEstate,
  PersonRelative,
  PersonReportData,
  PersonReportSectionCounts,
  PersonVehicle,
} from '../types';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toString(value: unknown): string {
  return value == null ? '' : String(value);
}

function toCountOrNull(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
}

function toNumberOrNull(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function mapContact(raw: unknown): PersonContactInfo | undefined {
  const r = asRecord(raw);
  if (!r) {
    return undefined;
  }

  const addresses = asArray(r.addresses)
    .map(asRecord)
    .filter((a): a is Record<string, unknown> => a !== null)
    .map((a) => ({ full: toString(a.full), type: toString(a.type) }))
    .filter((a) => a.full.length > 0);

  const emails = asArray(r.emails)
    .map(asRecord)
    .filter((e): e is Record<string, unknown> => e !== null)
    .map((e) => ({ address: toString(e.address), type: toString(e.type) }))
    .filter((e) => e.address.length > 0);

  const phones = asArray(r.phones)
    .map(asRecord)
    .filter((p): p is Record<string, unknown> => p !== null)
    .map((p) => ({ number: toString(p.number), type: toString(p.type) }))
    .filter((p) => p.number.length > 0);

  const socialProfiles = asArray(r.social_profiles)
    .map(toString)
    .filter(Boolean);
  const usernames = asArray(r.usernames).map(toString).filter(Boolean);

  if (
    !addresses.length &&
    !emails.length &&
    !phones.length &&
    !socialProfiles.length &&
    !usernames.length
  ) {
    return undefined;
  }

  return { addresses, emails, phones, socialProfiles, usernames };
}

function mapRelatives(raw: unknown): PersonRelative[] {
  const r = asRecord(raw);
  return asArray(r?.relatives)
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      id: toString(item.id),
      name: toString(item.name),
      address: toString(item.address),
      relationship: toString(item.relationship),
      aliases: asArray(item.aliases).map(toString).filter(Boolean),
      age: toNumberOrNull(item.age),
    }))
    .filter((item) => item.name.length > 0);
}

function mapAssociates(raw: unknown): PersonAssociate[] {
  const r = asRecord(raw);
  return asArray(r?.associates)
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      id: toString(item.id),
      name: toString(item.name),
      address: toString(item.address),
      aliases: asArray(item.aliases).map(toString).filter(Boolean),
      age: toNumberOrNull(item.age),
    }))
    .filter((item) => item.name.length > 0);
}

function mapNeighbors(raw: unknown): PersonNeighbor[] {
  const r = asRecord(raw);
  return asArray(r?.neighbors)
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      id: toString(item.id),
      name: toString(item.name),
      address: toString(item.address),
      age: toNumberOrNull(item.age),
    }))
    .filter((item) => item.name.length > 0);
}

function mapJobs(raw: unknown): PersonJob[] {
  const r = asRecord(raw);
  return asArray(r?.jobs)
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      company: toString(item.company),
      period: toString(item.period),
      titles: asArray(item.titles).map(toString).filter(Boolean),
    }))
    .filter((item) => item.company.length > 0 || item.titles.length > 0);
}

function mapEducations(raw: unknown): PersonEducation[] {
  const r = asRecord(raw);
  return asArray(r?.educations)
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      school: toString(item.school),
      attended: toString(item.attended),
      degrees: asArray(item.degrees).map(toString).filter(Boolean),
    }))
    .filter((item) => item.school.length > 0 || item.degrees.length > 0);
}

function mapLicenses(raw: unknown): PersonLicense[] {
  const r = asRecord(raw);
  return asArray(r?.licenses)
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      category: toString(item.category),
      type: toString(item.type),
      state: toString(item.state),
      status: toString(item.status),
      issued: toString(item.issued),
      expires: toString(item.expires),
    }));
}

function mapVehicles(raw: unknown): PersonVehicle[] {
  const r = asRecord(raw);
  return asArray(r?.vehicles)
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      make: toString(item.make),
      model: toString(item.model),
      year: toNumberOrNull(item.year),
    }))
    .filter((item) => item.make.length > 0 || item.model.length > 0);
}

function mapRealEstate(raw: unknown): PersonRealEstate[] {
  const r = asRecord(raw);
  return asArray(r?.real_estate)
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      fullAddress: toString(item.full_address ?? item.fullAddress),
      type: toString(item.type),
      lastSaleDate: toString(item.last_sale_date ?? item.lastSaleDate),
      marketValue: toNumberOrNull(item.market_value ?? item.marketValue),
      yearBuilt: toNumberOrNull(item.year_built ?? item.yearBuilt),
    }))
    .filter((item) => item.fullAddress.length > 0);
}

export function mapPersonReportPayload(
  value: Record<string, unknown>
): PersonReportData {
  const rawCounts = asRecord(value.sectionCounts) ?? {};

  const contact = mapContact(value.contact);
  const relatives = mapRelatives(value.familyAndAncestry);
  const associates = mapAssociates(value.associates);
  const neighbors = mapNeighbors(value.associates);
  const professionalJobs = mapJobs(value.professional);
  const professionalEducations = mapEducations(value.professional);
  const professionalLicenses = mapLicenses(value.professional);
  const vehicles = mapVehicles(value.assets);
  const realEstate = mapRealEstate(value.assets);

  const counts: PersonReportSectionCounts = {
    phones:
      toCountOrNull(value.phoneCount) ??
      toCountOrNull(rawCounts.phones) ??
      contact?.phones.length ??
      0,
    emails:
      toCountOrNull(value.emailCount) ??
      toCountOrNull(rawCounts.emails) ??
      contact?.emails.length ??
      0,
    addresses:
      toCountOrNull(value.addressCount) ??
      toCountOrNull(rawCounts.addresses) ??
      contact?.addresses.length ??
      0,
    socialProfiles:
      toCountOrNull(value.socialProfileCount) ??
      toCountOrNull(rawCounts.socialProfiles) ??
      contact?.socialProfiles.length ??
      0,
    usernames:
      toCountOrNull(value.usernameCount) ??
      toCountOrNull(rawCounts.usernames) ??
      contact?.usernames.length ??
      0,
    relatives:
      toCountOrNull(value.relativeCount) ??
      toCountOrNull(rawCounts.relatives) ??
      relatives.length,
    associates:
      toCountOrNull(value.associateCount) ??
      toCountOrNull(rawCounts.associates) ??
      associates.length,
    neighbors:
      toCountOrNull(value.neighborCount) ??
      toCountOrNull(rawCounts.neighbors) ??
      neighbors.length,
    jobs:
      toCountOrNull(value.jobCount) ??
      toCountOrNull(rawCounts.jobs) ??
      professionalJobs.length,
    educations:
      toCountOrNull(value.educationCount) ??
      toCountOrNull(rawCounts.educations) ??
      professionalEducations.length,
    licenses:
      toCountOrNull(value.licenseCount) ??
      toCountOrNull(rawCounts.licenses) ??
      professionalLicenses.length,
    vehicles:
      toCountOrNull(value.vehicleCount) ??
      toCountOrNull(rawCounts.vehicles) ??
      vehicles.length,
    realEstate:
      toCountOrNull(value.realEstateCount) ??
      toCountOrNull(rawCounts.realEstate) ??
      realEstate.length,
  };

  const rawAliases = Array.isArray(value.aliases) ? value.aliases : [];
  const aliases = rawAliases
    .filter((a): a is string => typeof a === 'string')
    .filter(Boolean);

  const rawSections = Array.isArray(value.availableSections)
    ? value.availableSections
    : [];
  const availableSections = rawSections.filter(
    (s): s is string => typeof s === 'string'
  );

  const ageRaw = value.age;
  const age = typeof ageRaw === 'number' && ageRaw > 0 ? ageRaw : null;

  return {
    name: toString(value.name),
    age,
    dob: toString(value.dob),
    location: toString(value.location),
    aliases,
    primaryAddress: toString(value.primaryAddress),
    primaryPhone: toString(value.primaryPhone),
    primaryEmail: toString(value.primaryEmail),
    job: toString(value.job),
    education: toString(value.education),
    sectionCounts: counts,
    availableSections,
    contact,
    relatives: relatives.length > 0 ? relatives : undefined,
    associates: associates.length > 0 ? associates : undefined,
    neighbors: neighbors.length > 0 ? neighbors : undefined,
    professionalJobs:
      professionalJobs.length > 0 ? professionalJobs : undefined,
    professionalEducations:
      professionalEducations.length > 0 ? professionalEducations : undefined,
    professionalLicenses:
      professionalLicenses.length > 0 ? professionalLicenses : undefined,
    vehicles: vehicles.length > 0 ? vehicles : undefined,
    realEstate: realEstate.length > 0 ? realEstate : undefined,
  };
}
